import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { reportTemplateHistoricalApi } from "@/main/api";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { ApiHistoryItem } from "@/main/models/response/apiHistoryItem";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { downloadFile } from "@/main/services/utilsFile";
import { firstLetterToUpperCase } from "@/main/services/utilsString";
import { IFormValuesRegisterReportTemplate } from "../registerReportTemplateScreen";
import * as S from "./styles";

export interface HistoryUIData {
	details: ApiHistoryItem[];
}

interface RegisterReportTemplateHistoricModalProps {
	isOpen: boolean;
	onClose: () => void;
	guidHistorical: string | null;
}

const RegisterReportTemplateHistoricModal: React.FC<RegisterReportTemplateHistoricModalProps> = ({
	isOpen,
	onClose,
	guidHistorical,
}) => {
	const { getValues } = useFormContext<IFormValuesRegisterReportTemplate>();
	const [historyUIData, setHistoryUIData] = useState<HistoryUIData | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchHistoryDetails = async () => {
			if (!isOpen || !guidHistorical) {
				setHistoryUIData(null);
				return;
			}
			setIsLoading(true);
			setError(null);
			try {
				const response = await reportTemplateHistoricalApi.getDetailHistoric(guidHistorical);
				const apiResponseData = response.data;
				const apiResult = apiResponseData.result || [];

				if (apiResult && apiResult.length > 0) {
					setHistoryUIData({ details: apiResult });
				} else {
					setHistoryUIData({ details: [] });
				}
				if (apiResponseData.errorMessages && apiResponseData.errorMessages.length > 0) {
					setError(apiResponseData.errorMessages.join("; "));
				}
			} catch (err) {
				const errorMessage = (err as any).response?.data?.error?.message || (err as any).message;
				setError(errorMessage);
				setHistoryUIData(null);
			} finally {
				setIsLoading(false);
			}
		};
		fetchHistoryDetails();
	}, [isOpen, guidHistorical]);

	const handleDownload = (result: ApiHistoryItem) => {
		if (isNullUndefined(result.fileImport)) return;
		const reportName = getValues("reportName") || "";
		const companyName = getValues("company.value") || "";
		downloadFile(
			result.fileImport.file,
			`${reportName} - ${companyName}`,
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		);
	};

	const renderValueContent = (
		value: string | { name: string; url: string; isDownloadLink?: boolean } | React.ReactNode,
		detailItem: ApiHistoryItem,
	): React.ReactNode => {
		if (isNullUndefined(value) || (typeof value === "string" && value === "")) {
			return "-";
		}
		if (typeof value === "string") {
			return value;
		}
		if (React.isValidElement(value)) {
			return value;
		}
		if (typeof value === "object" && "name" in value && "url" in value) {
			const linkData = value as {
				name: string;
				url: string;
				isDownloadLink?: boolean;
			};
			if (linkData.isDownloadLink) {
				const downloadText = getTranslateByKey("information.clickHereReportTemplate");
				return (
					<S.DownloadLink
						href="#"
						onClick={(e) => {
							e.preventDefault();
							handleDownload(detailItem);
						}}
					>
						{downloadText}
					</S.DownloadLink>
				);
			}
			return linkData.name;
		}
		return "-";
	};

	if (!isOpen) {
		return null;
	}

	const titleTypeHistory = historyUIData?.details?.[0]?.description || getTranslateByKey("information.action");

	return (
		<KogModalSideComponent
			open={isOpen}
			closeModal={onClose}
			title={getTranslateByKey("information.historyDetails")}
			id="id-report-template-history-modal"
		>
			{isLoading ? (
				<LoadingComponent />
			) : error ? (
				<div style={{ padding: "20px", textAlign: "center", color: "red" }}>{error}</div>
			) : historyUIData && historyUIData.details && historyUIData.details.length > 0 ? (
				<>
					<S.AccordionContainer>
						<S.AccordionTitle>{titleTypeHistory}</S.AccordionTitle>
						<S.AccordionDetailsHistory>
							{historyUIData.details.map((detailItem, index) => (
								<div
									key={detailItem.guidHistoric}
									style={{
										marginBottom: index === historyUIData.details.length - 1 ? 0 : "2rem",
									}}
								>
									<S.ContainerLabel>
										<S.LabelItem>
											{`${firstLetterToUpperCase(getTranslateByKey("SupplierInventoryHistory.field"))}:`}
										</S.LabelItem>
										<S.LabelResult>{renderValueContent(detailItem.field, detailItem)}</S.LabelResult>
									</S.ContainerLabel>
									<S.ContainerLabel>
										<S.LabelItem>{`${getTranslateByKey("information.from")}:`}</S.LabelItem>
										<S.LabelResult>
											{detailItem.isTemplateChanged && typeof detailItem.from === "string"
												? renderValueContent(
														{
															name: detailItem.from,
															url: "#",
															isDownloadLink: true,
														},
														detailItem,
													)
												: renderValueContent(detailItem.from, detailItem)}
										</S.LabelResult>
									</S.ContainerLabel>
									<S.ContainerLabel>
										<S.LabelItem>{`${getTranslateByKey("information.to")}:`}</S.LabelItem>
										<S.LabelResult>
											{detailItem.isTemplateChanged && typeof detailItem.to === "string"
												? renderValueContent(
														{
															name: detailItem.to,
															url: "#",
															isDownloadLink: true,
														},
														detailItem,
													)
												: renderValueContent(detailItem.to, detailItem)}
										</S.LabelResult>
									</S.ContainerLabel>
								</div>
							))}
						</S.AccordionDetailsHistory>
					</S.AccordionContainer>

					<div className="d-flex justify-content-center" style={{ marginTop: "10px" }}>
						<SecondaryButton id="id-report-template-history-back-button" onClick={onClose}>
							{getTranslateByKey("information.back")}
						</SecondaryButton>
					</div>
				</>
			) : (
				!isLoading &&
				!error && (
					<div style={{ padding: "20px", textAlign: "center" }}>
						{getTranslateByKey("historyDetails.noItemSelected")}
					</div>
				)
			)}
		</KogModalSideComponent>
	);
};

export default RegisterReportTemplateHistoricModal;
